import { useState } from "react";
import { toast } from "sonner";
import { Search, Reply, Share2, Trash2, Flag, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const C_STATUS = {
  new: "Nueva", notified_reporter: "Notificada", viewed_by_reporter: "Vista",
  reporter_replied: "Respondida", more_info_requested: "Solicitaste más info",
  contact_shared_by_responder: "Solicitante compartió contacto", contact_shared_by_reporter: "Compartiste tu contacto",
  contact_shared_mutual: "Contacto mutuo", discarded: "Descartada",
  abuse_reported: "Abuso reportado", closed: "Cerrada", blocked: "Bloqueada",
};

export default function LookupReport() {
  const [caseCode, setCaseCode] = useState("");
  const [mgmt, setMgmt] = useState("");
  const [data, setData] = useState(null);
  const [reply, setReply] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/cases/lookup", { case_code: caseCode, management_code: mgmt });
      setData(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const refresh = async () => {
    const { data } = await api.post("/cases/lookup", { case_code: caseCode, management_code: mgmt });
    setData(data);
  };

  const act = async (id, path, body, msg) => {
    try {
      await api.post(`/cases/manage/${path}`, { case_code: caseCode, management_code: mgmt, request_id: id, ...body });
      toast.success(msg); refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const doReply = (id, more) => {
    const m = (reply[id] || "").trim();
    if (!m) { toast.error("Escribe un mensaje."); return; }
    act(id, "reply", { message: m, more_info: more }, more ? "Solicitud enviada." : "Respuesta enviada.");
    setReply({ ...reply, [id]: "" });
  };

  if (!data) {
    return (
      <div className="max-w-md mx-auto space-y-5">
        <h1 className="font-heading text-2xl font-bold text-[#0F172A]">Consultar mi reporte</h1>
        <p className="text-sm text-[#475569]">Ingresa el código de caso y tu código privado de gestión para revisar solicitudes y responder. Si tienes cuenta, también puedes usar "Mis reportes".</p>
        <form onSubmit={load} className="space-y-3 bg-white border border-[#E5E7EB] rounded-2xl p-5">
          <label className="block"><span className="text-xs font-semibold text-[#475569]">Código de caso</span>
            <input value={caseCode} onChange={(e) => setCaseCode(e.target.value)} placeholder="RE-XXXXXX" data-testid="lookup-report-code"
              className="mt-1 w-full h-11 px-3 rounded-lg border border-[#E5E7EB] font-mono uppercase" /></label>
          <label className="block"><span className="text-xs font-semibold text-[#475569]">Código privado de gestión</span>
            <input value={mgmt} onChange={(e) => setMgmt(e.target.value)} placeholder="XXXXXXXX" data-testid="lookup-report-mgmt"
              className="mt-1 w-full h-11 px-3 rounded-lg border border-[#E5E7EB] font-mono uppercase" /></label>
          <button type="submit" disabled={busy} data-testid="lookup-report-submit"
            className="flex items-center justify-center gap-2 h-12 w-full rounded-xl bg-[#003893] text-white font-semibold disabled:opacity-50">
            <Search className="w-4 h-4" /> {busy ? "Consultando…" : "Consultar"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-4" data-testid="lookup-report-result">
      <button onClick={() => setData(null)} className="flex items-center gap-1 text-sm text-[#475569]"><ArrowLeft className="w-4 h-4" /> Consultar otro</button>
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
        <p className="font-mono font-bold text-[#003893]">{data.case.public_case_code}</p>
        <p className="text-sm text-[#475569]">{data.case.first_name || "Reporte"} · {data.case.status_label}</p>
      </div>
      <h2 className="font-heading font-semibold text-[#0F172A]">Solicitudes recibidas ({data.requests.length})</h2>
      {data.requests.length === 0 && <p className="text-sm text-[#94A3B8]">Aún no hay solicitudes.</p>}
      {data.requests.map((c) => (
        <div key={c.id} className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3 space-y-2" data-testid={`lookup-contact-${c.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold px-2 py-0.5 rounded bg-[#EFF6FF] text-[#1E40AF]">{c.request_type === "think_relative" ? "Cree ser familiar" : "Tiene información"}</span>
            <span className="text-xs font-semibold px-2 py-0.5 rounded bg-white border text-[#475569]">{C_STATUS[c.status] || c.status}</span>
          </div>
          <p className="text-sm font-medium">{c.requester_first_name} {c.requester_last_name || ""}</p>
          <p className="text-sm text-[#0F172A]">{c.message}</p>
          {c.responder_share_consent
            ? <p className="text-xs text-[#065F46]" data-testid={`lookup-shared-${c.id}`}>Autorizó compartir su contacto: {c.requester_phone || "—"} {c.requester_email ? `· ${c.requester_email}` : ""}</p>
            : <p className="text-xs text-[#94A3B8]">No autorizó compartir su contacto. Puedes responder de forma protegida.</p>}
          {c.responder_reply && <p className="text-xs text-[#475569]">Su respuesta: {c.responder_reply}</p>}
          {c.reporter_reply && <p className="text-xs text-[#475569]">Tu respuesta: {c.reporter_reply}</p>}
          {!["discarded", "closed", "blocked"].includes(c.status) && (
            <>
              <textarea value={reply[c.id] || ""} onChange={(e) => setReply({ ...reply, [c.id]: e.target.value })} rows={2}
                placeholder="Respuesta protegida…" data-testid={`lookup-reply-input-${c.id}`} className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm bg-white" />
              <div className="flex flex-wrap gap-2">
                <button onClick={() => doReply(c.id, false)} data-testid={`lookup-reply-${c.id}`} className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-[#003893] text-white text-xs font-semibold"><Reply className="w-3.5 h-3.5" /> Responder</button>
                <button onClick={() => doReply(c.id, true)} className="h-9 px-3 rounded-lg bg-[#FEF3C7] text-[#92400E] text-xs font-semibold">Pedir más info</button>
                {!c.reporter_share_consent && <button onClick={() => act(c.id, "share-contact", {}, "Contacto compartido.")} data-testid={`lookup-share-${c.id}`} className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-[#D1FAE5] text-[#065F46] text-xs font-semibold"><Share2 className="w-3.5 h-3.5" /> Compartir mi contacto</button>}
                <button onClick={() => act(c.id, "discard", {}, "Descartada.")} className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-[#F1F5F9] text-[#475569] text-xs font-semibold"><Trash2 className="w-3.5 h-3.5" /> Descartar</button>
                <button onClick={() => act(c.id, "abuse", {}, "Abuso reportado.")} className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-[#FEE2E2] text-[#991B1B] text-xs font-semibold"><Flag className="w-3.5 h-3.5" /> Abuso</button>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}
